import { AysArtificialRespirationHours } from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdRangeCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_ARTIFICIAL_RESPIRATION_HOURS_CONDITION_ID =
  'artificialRespirationHours';
export const FilterConditionArtificialRespirationHoursPluginType: FilterConditionPluginSpecification<
  AysArtificialRespirationHours,
  FilterConditionArtificialRespirationHoursDto,
  BdArtificialRespirationHoursCondition
> = {
  type: 'artificialRespirationHours',
  category: 'case',
  displayName: { de: 'Künstliche Beatmungsstunden' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionArtificialRespirationHoursDto,
  ): FilterConditionPluginImpl<AysArtificialRespirationHours> {
    return new FilterConditionArtificialRespirationHoursPlugin(
        AysArtificialRespirationHours.fromObject(
          obj.artificialRespirationHours,
        ),
      );
  },
  fromCondition(
    jsonObj: BdArtificialRespirationHoursCondition,
  ): FilterConditionPluginImpl<AysArtificialRespirationHours> {
    return new FilterConditionArtificialRespirationHoursPlugin(AysArtificialRespirationHours.fromObject(jsonObj));
  },
  newInstance(): FilterConditionPluginImpl<AysArtificialRespirationHours> {
    return new FilterConditionArtificialRespirationHoursPlugin(
        new AysArtificialRespirationHours(null, null),
      );
  },
};

export class FilterConditionArtificialRespirationHoursPlugin extends FilterConditionPluginImpl<
  AysArtificialRespirationHours,
  FilterConditionArtificialRespirationHoursDto
> {
  static override Specification: FilterConditionPluginSpecification<
    AysArtificialRespirationHours,
    FilterConditionArtificialRespirationHoursDto,
    BdArtificialRespirationHoursCondition
  > = FilterConditionArtificialRespirationHoursPluginType;

  constructor(
    private artificialRespirationHours: AysArtificialRespirationHours,
  ) {
    super(FilterConditionArtificialRespirationHoursPluginType.type);
  }

  clone(): FilterConditionPlugin<AysArtificialRespirationHours> {
    return new FilterConditionArtificialRespirationHoursPlugin(
      this.artificialRespirationHours.clone(),
    );
  }

  override equals(
    other: FilterConditionArtificialRespirationHoursPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(
        this.artificialRespirationHours,
        other.artificialRespirationHours,
        (a, b) => a.equals(b),
      )
    );
  }

  override valid(): boolean {
    if (!this.artificialRespirationHours) {
      return false;
    }

    if (
      Number.isInteger(this.artificialRespirationHours.to) &&
      Number.isInteger(this.artificialRespirationHours.from)
    ) {
      return (
        this.artificialRespirationHours.from <
        this.artificialRespirationHours.to
      );
    } else {
      return (
        Number.isInteger(this.artificialRespirationHours.to) ||
        Number.isInteger(this.artificialRespirationHours.from)
      );
    }
  }

  override validationToText(): string {
    if (!this.artificialRespirationHours) {
      return 'Kriterien für künstliche Beatmung auswählen';
    } else {
      return '';
    }
  }

  public toCondition(): BdCondition {
    return new BdArtificialRespirationHoursCondition(
        this.artificialRespirationHours.from,
        this.artificialRespirationHours.to,
      );
  }

  toJSON(): FilterConditionArtificialRespirationHoursDto | null {
    return {
      type: this.type,
      artificialRespirationHours: this.artificialRespirationHours,
    };
  }

  toText(): string | null {
    return 'Kriterien für künstliche Beatmung';
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const { from, to } = this.artificialRespirationHours ?? {};
    const items: string[] = [];
    const hasFrom = Number.isInteger(from);
    const hasTo = Number.isInteger(to);
    if (hasFrom && hasTo) {
      items.push(`von ${from} bis ${to} Stunden`);
    } else if (hasFrom) {
      items.push(`ab ${from} Stunden`);
    } else if (hasTo) {
      items.push(`bis ${to} Stunden`);
    }
    return { title, items };
  }

  toValue(): AysArtificialRespirationHours {
    return this.artificialRespirationHours;
  }
}

export class BdArtificialRespirationHoursCondition extends BdRangeCondition<number> {
  constructor(from: number, to: number) {
    super(BD_ARTIFICIAL_RESPIRATION_HOURS_CONDITION_ID, from, to);
  }
}

interface FilterConditionArtificialRespirationHoursDto {
  type: string;
  artificialRespirationHours: {from: number, to: number};
}
